import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Button,
  Flex,
  Checkbox,
  Text,
  Spinner,
  HStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Switch,
  useDisclosure,
  Progress,
  VStack,
} from "@chakra-ui/react";
import { supabase } from "../../../lib/supabaseClient";

const COLUMN_MAP = {
  email: "이메일",
  name: "이름",
  phone_number: "전화번호",
  business_account: "사업자 여부",
  business_bank_account_file: "통장 사본",
  business_reg_docu: "사업자 등록증",
  bank_account_holder: "예금주",
  bank_account_number: "계좌번호",
  bank_name: "은행명",
  role: "권한",
  partner: "파트너",
  admin: "관리자",
  created_date: "가입일",
};

const PAGE_SIZE = 20;
const SEARCHABLE = [
  "email",
  "name",
  "phone_number",
  "bank_account_holder",
  "bank_account_number",
];
const BUCKET = "business";

const EDITABLE_FIELDS = [
  "phone_number",
  "business_account",
  "business_bank_account_file",
  "business_reg_docu",
  "bank_account_holder",
  "bank_account_number",
  "bank_name",
  "role",
  "partner",
  "admin",
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({
    email: "",
    name: "",
    phone_number: "",
    bank_account_holder: "",
    bank_account_number: "",
    partner: false,
    admin: false,
  });
  const [searchInput, setSearchInput] = useState({
    email: "",
    name: "",
    phone_number: "",
    bank_account_holder: "",
    bank_account_number: "",
    partner: false,
    admin: false,
  });
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tempPage, setTempPage] = useState(page.toString());
  const [uploadProgress, setUploadProgress] = useState({ bank: 0, reg: 0 });
  const [uploadStatus, setUploadStatus] = useState("");

  useEffect(() => {
    fetchUsers(page);
    setTempPage(page.toString());
    // eslint-disable-next-line
  }, [page, search]);

  const fetchUsers = async (pageNum) => {
    setLoading(true);
    const from = (pageNum - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    let query = supabase
      .from("user")
      .select(
        "email, name, phone_number, business_account, business_bank_account_file, business_reg_docu, bank_account_holder, bank_account_number, bank_name, role, partner, admin, created_date",
        { count: "exact" }
      );
    // 검색 조건
    Object.entries(search).forEach(([key, value]) => {
      if (SEARCHABLE.includes(key) && value) {
        query = query.ilike(key, `%${value}%`);
      }
    });
    // 필터 조건 (소문자 변환 후 true와 비교)
    if (search.partner)
      query = query.or("partner.eq.true,partner.eq.TRUE,partner.eq.True");
    if (search.admin)
      query = query.or("admin.eq.true,admin.eq.TRUE,admin.eq.True");
    query = query.order("created_date", { ascending: false }).range(from, to);
    const { data, error, count } = await query;
    if (!error) {
      setUsers(data);
      setTotal(count);
    }
    setLoading(false);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Supabase Storage signed URL로 파일 다운로드
  const handleFileDownload = async (fileName, label) => {
    if (!fileName) return;
    try {
      // 과거 어드민 업로드분은 publicUrl이 아닌 버킷 상대 경로로 저장돼 있다
      const fileUrl = /^https?:\/\//i.test(fileName)
        ? fileName
        : supabase.storage.from(BUCKET).getPublicUrl(fileName).data?.publicUrl;
      if (!fileUrl) throw new Error("파일 경로를 확인할 수 없습니다.");
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("파일 다운로드 실패");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName.split("/").pop();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "다운로드 실패", status: "error", duration: 2000 });
    }
  };

  // 날짜 YYYY-MM-DD 포맷 함수
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    return d.toISOString().slice(0, 10);
  };

  // 검색 버튼 클릭 시
  const handleSearch = () => {
    setSearch({ ...searchInput });
    setPage(1);
  };

  // 행 클릭 시 수정 모달 오픈
  const handleRowClick = (user) => {
    setEditUser(user);
    setEditForm({ ...user });
    setUploadProgress({ bank: 0, reg: 0 });
    setUploadStatus("");
    onOpen();
  };

  // 수정 폼 값 변경
  const handleEditChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  // 저장 버튼 클릭 시
  const handleSave = async () => {
    if (!editUser) return;
    setSaving(true);
    const updateData = {};
    EDITABLE_FIELDS.forEach((key) => {
      if (key === "admin" || key === "partner") {
        updateData[key] = String(editForm[key]).toLowerCase();
      } else {
        updateData[key] = editForm[key];
      }
    });
    // RLS로 막히면 에러 없이 0건이 반환되므로 select로 반영 건수를 확인한다
    const { data, error } = await supabase
      .from("user")
      .update(updateData)
      .eq("email", editUser.email)
      .select("email");
    setSaving(false);
    if (error) {
      toast({
        title: "수정 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!data || data.length === 0) {
      toast({
        title: "수정 권한이 없습니다",
        description: "관리자 권한이 있는 계정으로 다시 시도해주세요.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    toast({ title: "수정 완료", status: "success", duration: 1500 });
    onClose();
    fetchUsers(page);
  };

  const handlePageInput = (e) => setTempPage(e.target.value);
  const handlePageMove = () => {
    if (/^\d+$/.test(tempPage)) {
      const p = parseInt(tempPage);
      if (p >= 1 && p <= totalPages) setPage(p);
      else setTempPage(page.toString());
    } else setTempPage(page.toString());
  };

  const uploadFile = async (file, type) => {
    if (!file) return null;
    try {
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const timestamp = new Date().getTime();
      const fileName = `${type}_${timestamp}.${fileExt}`;
      const filePath = `${editUser.email}/${fileName}`;

      setUploadStatus(`"${file.name}" 파일 업로드 중...`);

      const { data, error: urlError } = await supabase.storage
        .from(BUCKET)
        .createSignedUploadUrl(filePath);

      if (urlError || !data?.signedUrl) {
        console.error("URL 발급 오류:", urlError);
        throw new Error("Signed URL 발급 실패");
      }

      const signedUrl = data.signedUrl;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader(
          "Content-Type",
          file.type || "application/octet-stream"
        );

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress((prev) => ({
              ...prev,
              [type]: Math.round((e.loaded / e.total) * 100),
            }));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`업로드 실패: ${xhr.status} ${xhr.statusText}`));
          }
        };
        xhr.onerror = () => reject(new Error("네트워크 오류가 발생했습니다."));
        xhr.send(file);
      });

      const { data: publicUrlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(filePath);

      setUploadProgress((prev) => ({ ...prev, [type]: 100 }));
      setUploadStatus(
        `"${file.name}" 업로드 완료 — [저장] 버튼을 눌러야 회원 정보에 반영됩니다.`
      );

      return publicUrlData?.publicUrl ?? filePath;
    } catch (error) {
      console.error("파일 업로드 중 오류 발생:", error);
      setUploadStatus("");
      setUploadProgress((prev) => ({ ...prev, [type]: 0 }));
      toast({
        title: "파일 업로드 실패",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const filePath = await uploadFile(file, type);
    if (filePath) {
      handleEditChange(
        type === "bank" ? "business_bank_account_file" : "business_reg_docu",
        filePath
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box p={6} bg="white">
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        회원 목록
      </Text>
      {/* 검색/필터 UI */}
      <Flex mb={4} gap={2} flexWrap="wrap" align="center">
        {SEARCHABLE.map((key) => (
          <Input
            key={key}
            size="sm"
            width="180px"
            placeholder={`${COLUMN_MAP[key]} 검색`}
            value={searchInput[key]}
            onChange={(e) =>
              setSearchInput((s) => ({ ...s, [key]: e.target.value }))
            }
            onKeyPress={handleKeyPress}
            fontSize="sm"
            bg="white"
          />
        ))}
        <Checkbox
          isChecked={searchInput.partner}
          onChange={(e) =>
            setSearchInput((s) => ({ ...s, partner: e.target.checked }))
          }
          fontSize="sm"
        >
          파트너만
        </Checkbox>
        <Checkbox
          isChecked={searchInput.admin}
          onChange={(e) =>
            setSearchInput((s) => ({ ...s, admin: e.target.checked }))
          }
          fontSize="sm"
        >
          관리자만
        </Checkbox>
        <Button size="sm" onClick={handleSearch} colorScheme="blue" ml={2}>
          검색
        </Button>
      </Flex>
      {/* 테이블 */}
      <Box
        overflowX="auto"
        borderRadius="md"
        borderWidth={1}
        borderColor="gray.200"
        bg="white"
      >
        <Table size="sm" minWidth="1200px" fontSize="xs" tableLayout="fixed">
          <Thead>
            <Tr>
              {Object.entries(COLUMN_MAP).map(([key, label]) => (
                <Th
                  key={key}
                  bg="gray.100"
                  fontSize="xs"
                  fontWeight="bold"
                  textAlign="center"
                  whiteSpace="nowrap"
                  p={2}
                >
                  {label}
                </Th>
              ))}
              <Th
                bg="gray.100"
                fontSize="xs"
                fontWeight="bold"
                textAlign="center"
                whiteSpace="nowrap"
                p={2}
              >
                관리
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td
                  colSpan={Object.keys(COLUMN_MAP).length + 1}
                  textAlign="center"
                  py={8}
                >
                  <Spinner size="sm" mr={2} /> 불러오는 중...
                </Td>
              </Tr>
            ) : users.length === 0 ? (
              <Tr>
                <Td
                  colSpan={Object.keys(COLUMN_MAP).length + 1}
                  textAlign="center"
                  py={8}
                >
                  데이터가 없습니다.
                </Td>
              </Tr>
            ) : (
              users.map((user, idx) => (
                <Tr key={user.email + idx}>
                  {Object.keys(COLUMN_MAP).map((col) => (
                    <Td
                      key={col}
                      fontSize="xs"
                      textAlign="center"
                      p={2}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      maxW="160px"
                    >
                      {col === "business_bank_account_file" ? (
                        user[col] ? (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDownload(user[col], "통장사본");
                            }}
                          >
                            통장 사본 다운로드
                          </Button>
                        ) : (
                          "-"
                        )
                      ) : col === "business_reg_docu" ? (
                        user[col] ? (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDownload(user[col], "사업자등록증");
                            }}
                          >
                            사업자 등록증 다운로드
                          </Button>
                        ) : (
                          "-"
                        )
                      ) : col === "created_date" ? (
                        formatDate(user[col])
                      ) : user[col] === null || user[col] === undefined ? (
                        "-"
                      ) : (
                        String(user[col])
                      )}
                    </Td>
                  ))}
                  <Td textAlign="center" p={2}>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      onClick={() => handleRowClick(user)}
                    >
                      수정
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {/* 페이지네이션 */}
      <HStack mt={6} justify="center" spacing={2}>
        <Button
          size="xs"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          이전
        </Button>
        <HStack spacing={1} mx={2}>
          <Input
            value={tempPage}
            onChange={handlePageInput}
            size="xs"
            width="40px"
            textAlign="center"
            fontSize="xs"
          />
          <Button
            size="xs"
            onClick={handlePageMove}
            colorScheme="gray"
            variant="outline"
            minW="32px"
            px={2}
            color="gray.600"
            borderColor="gray.300"
            _hover={{ bg: "gray.100", borderColor: "gray.400" }}
          >
            이동
          </Button>
          <Text fontSize="sm">/ {totalPages}</Text>
        </HStack>
        <Button
          size="xs"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
        >
          다음
        </Button>
      </HStack>

      {/* 수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>회원 정보 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editUser && (
              <Box as="form">
                <FormControl mb={2}>
                  <FormLabel>이메일</FormLabel>
                  <Input value={editUser.email} isReadOnly size="sm" />
                </FormControl>
                <FormControl mb={2}>
                  <FormLabel>이름</FormLabel>
                  <Input value={editUser.name} isReadOnly size="sm" />
                </FormControl>
                <FormControl mb={2}>
                  <FormLabel>가입일</FormLabel>
                  <Input
                    value={formatDate(editUser.created_date)}
                    isReadOnly
                    size="sm"
                  />
                </FormControl>
                {EDITABLE_FIELDS.map((key) => (
                  <FormControl mb={2} key={key}>
                    <FormLabel>{COLUMN_MAP[key]}</FormLabel>
                    {key === "partner" ||
                    key === "admin" ||
                    key === "business_account" ? (
                      <Switch
                        isChecked={
                          String(editForm[key]).trim().toLowerCase() === "true"
                        }
                        onChange={(e) =>
                          handleEditChange(
                            key,
                            e.target.checked ? "true" : "false"
                          )
                        }
                        colorScheme="blue"
                        size="md"
                      />
                    ) : key === "business_bank_account_file" ? (
                      <Box>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, "bank")}
                          size="sm"
                          mb={2}
                        />
                        {editForm[key] && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="link"
                            onClick={(e) => {
                              e.preventDefault();
                              handleFileDownload(editForm[key], "통장사본");
                            }}
                          >
                            현재 파일 다운로드
                          </Button>
                        )}
                      </Box>
                    ) : key === "business_reg_docu" ? (
                      <Box>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileChange(e, "reg")}
                          size="sm"
                          mb={2}
                        />
                        {editForm[key] && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            variant="link"
                            onClick={(e) => {
                              e.preventDefault();
                              handleFileDownload(editForm[key], "사업자등록증");
                            }}
                          >
                            현재 파일 다운로드
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Input
                        value={editForm[key] ?? ""}
                        onChange={(e) => handleEditChange(key, e.target.value)}
                        size="sm"
                      />
                    )}
                  </FormControl>
                ))}
                {uploadStatus && (
                  <Box mt={4} p={3} bg="blue.50" borderRadius="md">
                    <Text mb={2} fontSize="sm" color="blue.700">
                      {uploadStatus}
                    </Text>
                    <VStack spacing={2}>
                      {uploadProgress.bank > 0 && (
                        <Box width="100%">
                          <Text fontSize="xs" mb={1} color="blue.600">
                            통장 사본 업로드 진행률
                          </Text>
                          <Progress
                            value={uploadProgress.bank}
                            size="xs"
                            colorScheme={
                              uploadProgress.bank === 100 ? "green" : "blue"
                            }
                            borderRadius="full"
                            hasStripe={uploadProgress.bank < 100}
                            isAnimated={uploadProgress.bank < 100}
                          />
                        </Box>
                      )}
                      {uploadProgress.reg > 0 && (
                        <Box width="100%">
                          <Text fontSize="xs" mb={1} color="blue.600">
                            사업자 등록증 업로드 진행률
                          </Text>
                          <Progress
                            value={uploadProgress.reg}
                            size="xs"
                            colorScheme={
                              uploadProgress.reg === 100 ? "green" : "blue"
                            }
                            borderRadius="full"
                            hasStripe={uploadProgress.reg < 100}
                            isAnimated={uploadProgress.reg < 100}
                          />
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={3} size="sm">
              취소
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              size="sm"
            >
              저장
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Users;
